import { useCallback, useEffect, useMemo } from "react";

import { useFirebaseMessagingContext } from "@/context/Chat";

import { useAppDispatch, useAppSelector } from "@/store/hooks";

import { storage } from "../Storage";

import { CONVERSATION_TYPE } from "@/models/chat";
import { IRoleType } from "@/models/role";
import { ISessionKeys } from "@/models";
import { IModalKeyEnums, IUserPermissionEnums } from "@/models/permission";

import { usePermissionRestriction } from "./usePermissionRestriction";
import { filterOutUndefined } from "..";

import { appointmentDetails } from "@/store/slices/doctor/appointment";
import { ipDashboardDetails } from "@/store/slices/doctor/ipDashboard";
import { videoAppointmentDetails } from "@/store/slices/videoAppointment";
import { chat, fetchUser, setSelectedChat } from "@/store/slices/chat";

import useIsMobile from "./useIsMobile";
import useIsTablet from "./useIsTablet";

const useSelectChat = () => {
  const { userDetails, selectedChat } = useAppSelector(chat);
  const { appointmentDetail } = useAppSelector(appointmentDetails);
  const { patientDetail } = useAppSelector(ipDashboardDetails);
  const { appointmentDetail: videoAppointmentDetail } = useAppSelector(
    videoAppointmentDetails
  );

  const currentUserId = storage.getSessionToken(ISessionKeys.USER_ID);
  const roleType = storage.getSessionToken(ISessionKeys.ROLE_TYPE);

  const { handleChannel, enrichedChannelList, paginatedDocs, channelId } =
    useFirebaseMessagingContext();

  const dispatch = useAppDispatch();

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const isMobileOrTablet = isMobile || isTablet;

  const permissionRestrictionHandler = usePermissionRestriction();

  const chatType = useMemo(() => {
    return appointmentDetail?.id ?? videoAppointmentDetail?.id
      ? CONVERSATION_TYPE.APPOINTMENT
      : CONVERSATION_TYPE.IN_PATIENT;
  }, [appointmentDetail, patientDetail, videoAppointmentDetail]);

  const { patientIds, memberIds } = useMemo(() => {
    const patientIds = Array.from(
      new Set(
        enrichedChannelList?.map((item: any) => item.users.patient_id) || []
      )
    );
    const memberIds = Array.from(
      new Set(
        enrichedChannelList?.map((item: any) => item.users.patient_member_id) ||
          []
      )
    );
    return { patientIds, memberIds };
  }, [enrichedChannelList]);

  const staffMembersIds = useCallback(
    (msgDocumentId: string) => {
      const ids = enrichedChannelList?.find(
        (channel: any) => channel?.uniqueId === msgDocumentId
      );
      const uniqueIds = Array.from(new Set(ids?.users?.staff_members || []));
      const uniqueIdsWithoutCurrentUser = uniqueIds.filter(
        (id: string) => id !== currentUserId
      );
      return uniqueIdsWithoutCurrentUser;
    },
    [enrichedChannelList, currentUserId]
  );

  const inpatientDetails = useMemo(() => {
    const inpatientIds: string[] = [];

    enrichedChannelList?.forEach((item: any) => {
      if (item.type === CONVERSATION_TYPE.IN_PATIENT) {
        inpatientIds.push(item.id);
      }
    });
    return inpatientIds;
  }, [enrichedChannelList]);

  const getStaffMembersIds = useCallback(() => {
    if (Object.keys(selectedChat || {}).length === 0) return [];
    const ids = selectedChat?.users?.staff_members || [];
    const uniqueIds = Array.from(new Set(ids));
    const uniqueIdsWithoutCurrentUser = uniqueIds.filter(
      (id: string) => id !== currentUserId
    );
    return uniqueIdsWithoutCurrentUser;
  }, [selectedChat, currentUserId]);

  // Memoize the function
  const handleChannelMemoized = useCallback(
    async (id: string) => {
      if (id) {
        handleChannel(id);
        const isFetchDoctorTeamPermissionGranted = permissionRestrictionHandler(
          IModalKeyEnums.USER,
          IUserPermissionEnums.FETCH_DETAILS_FOR_CHAT
        );
        if (!isFetchDoctorTeamPermissionGranted) return;
        await dispatch(
          fetchUser({
            doctor_ids: filterOutUndefined(getStaffMembersIds()) as string[],
            appointment_ids: [selectedChat?.id] as string[],
            device_token_user_id: currentUserId!,
            user_ids: [selectedChat?.users?.patient_id] as string[],
            ip_dash_id: [selectedChat?.id] as string[],
            member_ids: [selectedChat?.users?.patient_member_id] as string[],
          })
        );
      }
    },
    [
      handleChannel,
      chatType,
      appointmentDetail,
      patientDetail,
      selectedChat?.id,
    ]
  );

  useEffect(() => {
    if (
      Object.keys(userDetails)?.length > 0 &&
      roleType !== IRoleType.PATIENT
    ) {
      let selectedChatInfo: any;
      if (isMobileOrTablet) {
        if (Object.keys(selectedChat)?.length > 0) {
          selectedChatInfo = selectedChat;
        } else {
          selectedChatInfo = paginatedDocs?.find(
            (item: any) => item.uniqueId === channelId
          );
        }
      } else {
        selectedChatInfo = selectedChat;
      }
      const staffMembersId = selectedChatInfo?.users?.staff_members?.filter(
        (item: string) => item !== currentUserId
      );
      const doctorDetail = userDetails?.doctor_details?.filter((d: any) =>
        staffMembersId?.includes(d.user_id)
      );

      const detail = userDetails?.user_details?.find(
        (d: any) => d.patientId === selectedChatInfo?.users?.patient_id
      );
      const ipDashDetail = userDetails?.inpatient_details?.find(
        (d: any) =>
          d.id === (selectedChatInfo?.id ?? selectedChatInfo?.hospitalPatientId)
      );
      const memberDetails = userDetails?.member_details?.find(
        (d: any) =>
          d.memberId ===
          (selectedChatInfo?.users?.patient_member_id ??
            selectedChatInfo?.memberId)
      );

      dispatch(
        setSelectedChat({
          ...selectedChatInfo,
          doctor_details:
            roleType === IRoleType.PATIENT ? doctorDetail?.at(0) : doctorDetail,
          user_details: detail,
          inpatient_details: ipDashDetail,
          member_details: memberDetails,
        })
      );
    }
  }, [userDetails, channelId]);

  return {
    handleChannelMemoized,
    chatType,
    staffMembersIds,
    patientIds,
    memberIds,
    inpatientDetails,
  };
};

export default useSelectChat;
